import './IconLocalLinearMappings.scss';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IconLocalMappings } from '@blisspointmedia/bpm-types/dist/LinearBuying';
import { ModalEditWidget } from '@Components/ModalEditWidget';
import { SaveChangesInput } from '@Components/ModalEditWidget/modal-edit-constants';
import { Page } from '@Components/Page';
import { Spinner } from '@Components/Spinner';
import { TableDensity } from '@Components/StickyTable';
import { useSetError } from '@src/redux/modals';
import { saveIconLocalMappings, useIconLocalMappings } from './use-icon-local-mappings';

const twoColHeaders = [
  { label: 'Input', field: 'input', flex: true, modalRow: 0, modalWidth: 300 },
  { label: 'Output', field: 'output', flex: true, modalRow: 0, modalWidth: 300 }
];

const networkHeaders = [
  { label: 'Input', field: 'input', flex: true, modalRow: 0, modalWidth: 250 },
  { label: 'Market', field: 'market', flex: true, modalRow: 0, modalWidth: 250 },
  { label: 'Output', field: 'output', flex: true, modalRow: 0, modalWidth: 250 }
];

const dowHeaders = [
  { label: 'Input', field: 'input', flex: true, modalRow: 0, modalWidth: 200 },
  { label: 'String Output', field: 'string_output', flex: true, modalRow: 0, modalWidth: 200 },
  { label: 'Range Output', field: 'range_output', flex: true, modalRow: 0, modalWidth: 200 }
];

interface TableConfig {
  key: keyof IconLocalMappings;
  title: string;
  headers: { label: string; field: string; modalRow: number; modalWidth: number }[];
}

const TABLE_CONFIGS: TableConfig[] = [
  { key: 'network_map', title: 'Network Map', headers: networkHeaders },
  { key: 'market_map', title: 'Market Map', headers: twoColHeaders },
  { key: 'daypart_map', title: 'Daypart Map', headers: twoColHeaders },
  { key: 'dow_map', title: 'Day of Week Map', headers: dowHeaders }
];

// Rows carry a session-local rowIdx so save deltas can be matched back to the
// working copy even when the edited row's key fields changed. Stripped before POST.
type TaggedRow<K extends keyof IconLocalMappings> = IconLocalMappings[K][number] & {
  rowIdx: number;
};
type TaggedMappings = { [K in keyof IconLocalMappings]: TaggedRow<K>[] };
type AnyMappingRow = TaggedRow<keyof IconLocalMappings>;

const tagRows = (mappings: IconLocalMappings): TaggedMappings => ({
  network_map: mappings.network_map.map((row, rowIdx) => ({ ...row, rowIdx })),
  market_map: mappings.market_map.map((row, rowIdx) => ({ ...row, rowIdx })),
  daypart_map: mappings.daypart_map.map((row, rowIdx) => ({ ...row, rowIdx })),
  dow_map: mappings.dow_map.map((row, rowIdx) => ({ ...row, rowIdx }))
});

const untagRows = (mappings: TaggedMappings): IconLocalMappings => ({
  network_map: mappings.network_map.map(({ rowIdx: _rowIdx, ...row }) => row),
  market_map: mappings.market_map.map(({ rowIdx: _rowIdx, ...row }) => row),
  daypart_map: mappings.daypart_map.map(({ rowIdx: _rowIdx, ...row }) => row),
  dow_map: mappings.dow_map.map(({ rowIdx: _rowIdx, ...row }) => row)
});

const applyDeltas = (
  current: AnyMappingRow[],
  { addedData, modifiedData, deletedData }: SaveChangesInput<AnyMappingRow>
): AnyMappingRow[] => {
  const deletedIdx = new Set(deletedData.map(row => row.rowIdx));
  const modifiedByIdx = new Map(modifiedData.map(row => [row.rowIdx, row]));
  return [
    ...current
      .filter(row => !deletedIdx.has(row.rowIdx))
      .map(row => modifiedByIdx.get(row.rowIdx) ?? row),
    ...addedData
  ];
};

export const IconLocalLinearMappings = () => {
  const setError = useSetError();
  const { data: fetchedData, isLoading, error } = useIconLocalMappings();
  // Post-save working copy; undefined until the first successful save, after
  // which it takes over from the freshly-tagged query data.
  const [savedData, setSavedData] = useState<TaggedMappings>();
  const [saving, setSaving] = useState(false);

  const data = useMemo(
    () => savedData ?? (fetchedData ? tagRows(fetchedData) : undefined),
    [savedData, fetchedData]
  );

  // The save handler closes over a ref so per-key callbacks can stay stable
  // (I2) without going stale when data changes.
  const dataRef = useRef<TaggedMappings | undefined>(undefined);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  useEffect(() => {
    if (error) {
      setError({
        message: `Failed to get ICON local mappings data: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  const handleSave = useCallback(
    async (key: keyof IconLocalMappings, input: SaveChangesInput<AnyMappingRow>) => {
      const { current } = dataRef;
      if (!current) return;
      const updated: TaggedMappings = { ...current, [key]: applyDeltas(current[key], input) };
      try {
        setSaving(true);
        await saveIconLocalMappings(untagRows(updated));
        setSavedData(updated);
        input.cleanup();
      } catch (e) {
        setError({
          message: `Failed to save ICON local mappings data: ${e.message}`,
          reportError: e
        });
      } finally {
        setSaving(false);
      }
    },
    [setError]
  );

  const saveHandlers = useMemo(
    () =>
      new Map(
        TABLE_CONFIGS.map(({ key }) => [
          key,
          (input: SaveChangesInput<AnyMappingRow>) => handleSave(key, input)
        ])
      ),
    [handleSave]
  );

  return (
    <Page
      title='ICON Local Linear Mappings'
      pageType='ICON Local Linear Mappings'
      minHeight='600px'
      isLoading={isLoading}
      actions={saving ? <Spinner color='white' /> : undefined}
    >
      <MappingsBody data={data} isLoading={isLoading} error={error} saveHandlers={saveHandlers} />
    </Page>
  );
};

interface MappingsBodyProps {
  data: TaggedMappings | undefined;
  isLoading: boolean;
  error: Error | null;
  saveHandlers: Map<keyof IconLocalMappings, (input: SaveChangesInput<AnyMappingRow>) => void>;
}

const MappingsBody = ({ data, isLoading, error, saveHandlers }: MappingsBodyProps) => {
  const showSkeleton = isLoading || (!data && !error);
  if (!data && !showSkeleton) return undefined;

  return (
    <div className='icon-local-linear-mappings-page-container'>
      {TABLE_CONFIGS.map(({ key, title, headers }) => (
        <div key={key} className='icon-local-linear-mappings-section'>
          <ModalEditWidget<AnyMappingRow>
            className='icon-local-linear-mappings-table'
            name={title}
            headers={headers}
            initialTableData={data?.[key] ?? []}
            isLoading={showSkeleton}
            selectorOptions={{}}
            onSaveModal={saveHandlers.get(key)}
            filterBar
            density={TableDensity.DEFAULT}
          />
        </div>
      ))}
    </div>
  );
};
